/**
 * 图片打包下载服务模块
 * 处理批量图片ZIP打包和下载功能
 */

import { apiService } from './api'
import type {
  CreateImagePackRequest,
  CreateImagePackResponse,
  ImagePackTask,
  ImagePackTaskListResponse,
  ImagePackTaskListParams,
  ImagePackApiResponse,
} from '@/types/imagePack'

const IMAGE_PACK_ENDPOINT = '/api/user/image-packs' as const

/**
 * 创建图片打包任务
 * @param params 创建参数
 * @returns 打包任务信息
 */
export async function createImagePackTask(
  params: CreateImagePackRequest
): Promise<CreateImagePackResponse> {
  const response = await apiService.post<
    ImagePackApiResponse<CreateImagePackResponse>
  >(IMAGE_PACK_ENDPOINT, params)
  return response.data
}

/**
 * 获取图片打包任务状态
 * @param taskId 打包任务ID
 * @returns 任务详情
 */
export async function getImagePackTask(
  taskId: string
): Promise<ImagePackTask> {
  const response = await apiService.get<ImagePackApiResponse<ImagePackTask>>(
    `${IMAGE_PACK_ENDPOINT}/${taskId}`
  )
  return response.data
}

/**
 * 获取用户的图片打包任务列表
 * @param params 查询参数
 * @returns 任务列表
 */
export async function listImagePackTasks(
  params: ImagePackTaskListParams = {}
): Promise<ImagePackTaskListResponse> {
  const queryParams: Record<string, string> = {
    page: String(params.page ?? 1),
    page_size: String(params.page_size ?? 10),
  }

  if (params.ref_type !== undefined) {
    queryParams['ref_type'] = params.ref_type
  }
  if (params.ref_id !== undefined && params.ref_id !== '') {
    queryParams['ref_id'] = params.ref_id
  }
  if (params.status !== undefined) {
    queryParams['status'] = params.status
  }

  const response = await apiService.get<
    ImagePackApiResponse<ImagePackTaskListResponse>
  >(IMAGE_PACK_ENDPOINT, queryParams)
  return response.data
}

/**
 * 轮询图片打包任务状态
 * @param taskId 打包任务ID
 * @param onUpdate 状态更新回调
 * @param interval 轮询间隔(毫秒)，默认2000ms
 * @returns 取消轮询的函数
 */
export function pollImagePackTask(
  taskId: string,
  onUpdate: (task: ImagePackTask) => void,
  interval = 5000
): () => void {
  let isActive = true
  let timeoutId: number | null = null
  const canContinuePolling = (): boolean => isActive

  const poll = async (): Promise<void> => {
    if (!canContinuePolling()) return

    try {
      const task = await getImagePackTask(taskId)
      onUpdate(task)

      // 如果任务已完成或失败,停止轮询
      if (task.status === 'completed' || task.status === 'failed') {
        isActive = false
        return
      }

      // 继续轮询
      if (canContinuePolling()) {
        timeoutId = window.setTimeout((): void => {
          void poll()
        }, interval)
      }
    } catch (error) {
      console.error('[ImagePackService] 轮询任务状态失败:', error)
      // 出错时继续轮询
      if (canContinuePolling()) {
        timeoutId = window.setTimeout((): void => {
          void poll()
        }, interval)
      }
    }
  }

  // 开始轮询
  void poll()

  // 返回取消函数
  return (): void => {
    isActive = false
    if (timeoutId !== null) {
      window.clearTimeout(timeoutId)
    }
  }
}
